import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, MEDIA, TALENT_ROADMAP } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const Services = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <main data-testid="page-services">
      {/* Header */}
      <section className="relative pt-40 pb-20 overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute -top-32 right-0 w-[600px] h-[400px] glow-amber pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-6">
              Capabilities
            </div>
            <h1 className="font-display text-stone-900 text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter mb-8 max-w-5xl">
              Eight practices.<br />
              <span className="italic text-[#B8860B]">One senior bench.</span>
            </h1>
            <p className="text-stone-600 text-lg md:text-xl max-w-2xl leading-relaxed">
              Whether you need to ship a generative-AI copilot, migrate a complex SAP estate or rebuild your HR function from the ground up — we bring the seniors and the playbooks to get it done.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Service detail rows */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 space-y-20 md:space-y-28">
          {SERVICES.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <Reveal key={s.id} id={s.id} className="scroll-mt-28" data-testid={`service-detail-${s.id}`}>
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:flex-row-reverse" : ""}`}>
                  <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                    <div className="relative aspect-[4/5] overflow-hidden bento-card group">
                      <img
                        src={i % 3 === 0 ? MEDIA.aiTech : i % 3 === 1 ? MEDIA.team : MEDIA.office}
                        alt={s.title}
                        className="absolute inset-0 w-full h-full object-cover opacity-95 pencil group-hover:scale-105 transition-transform duration-1000"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <s.icon className="w-10 h-10 text-[#B8860B] mb-4" strokeWidth={1.25} />
                        <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B]">{`0${i + 1}`}</div>
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-7">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                      {s.tagline}
                    </div>
                    <h2 className="font-display text-stone-900 text-4xl md:text-5xl font-light tracking-tighter mb-6">
                      {s.title}
                    </h2>
                    <p className="text-stone-700 text-lg leading-relaxed mb-8 max-w-2xl">
                      {s.description}
                    </p>

                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                      {s.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-stone-700">
                          <Check className="w-5 h-5 text-[#B8860B] mt-0.5 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contact"
                      data-testid={`service-detail-cta-${s.id}`}
                      className="inline-flex items-center gap-2 text-[#B8860B] hover:text-[#B8860B] link-underline"
                    >
                      Discuss a {s.title.toLowerCase()} engagement <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Talent Acquisition Roadmap */}
      <section className="py-24 md:py-32 border-t border-stone-200 bg-[#F5F1EA]" id="talent-roadmap">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
              How we deliver
            </div>
            <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-6 max-w-3xl">
              Talent Acquisition<br />Roadmap.
            </h2>
            <p className="text-stone-600 text-lg max-w-2xl mb-16 leading-relaxed">
              An AI-enabled, six-step process that consistently lands the right candidate — from JD to joining — across our recruitment and RPO engagements.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
            {TALENT_ROADMAP.map((s, i) => (
              <Reveal key={s.step} delay={i * 70} className="bg-white p-8 lg:p-10" data-testid={`roadmap-step-${i}`}>
                <div className="font-mono text-[#B8860B] text-sm mb-5 tracking-wider">{s.step}</div>
                <h3 className="font-display text-xl text-stone-900 mb-3 font-medium">{s.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
